import React from 'react';
import { useTranslation } from 'react-i18next';
import { MoreVertical, Pencil, Trash2, ToggleLeft, ToggleRight } from 'lucide-react';
import { Menu } from '../../../services/menus/menusService';
import { useState, useRef, useEffect } from 'react';

interface MenuListProps {
  menus: Menu[];
  selectedMenuId: string | null;
  menuItemCounts: Record<string, number>;
  onSelect: (menuId: string) => void;
  onEdit: (menu: Menu) => void;
  onDelete: (menu: Menu) => void;
  onToggleActive: (menu: Menu) => void;
}

const MenuList: React.FC<MenuListProps> = ({
  menus,
  selectedMenuId,
  menuItemCounts,
  onSelect,
  onEdit,
  onDelete,
  onToggleActive,
}) => {
  const { t } = useTranslation();
  const [openKebabId, setOpenKebabId] = useState<string | null>(null);
  const kebabRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (kebabRef.current && !kebabRef.current.contains(event.target as Node)) {
        setOpenKebabId(null);
      }
    };
    if (openKebabId) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openKebabId]);

  return (
    <div className="flex flex-col h-full">
      {/* Menu List */}
      <div className="flex-1 overflow-y-auto p-3 space-y-2">
        {menus.length === 0 ? (
          <p className="text-sm text-zinc-500 dark:text-zinc-400 text-center py-8 px-4">
            {t('menus.noMenus')}
          </p>
        ) : (
          menus.map((menu) => (
            <div
              key={menu.id}
              onClick={() => onSelect(menu.id)}
              className={`relative p-3 rounded-xl cursor-pointer transition-colors border ${
                selectedMenuId === menu.id
                  ? 'bg-[#EDFCF5] dark:bg-[#0E8965]/10 border-[#0E8965]/30 dark:border-[#0E8965]/30'
                  : 'bg-white dark:bg-background-dark border-zinc-100 dark:border-borderPrimary-dark hover:bg-zinc-50 dark:hover:bg-backgroundDarker-dark'
              }`}
            >
              <div className="flex items-start justify-between">
                <div className="min-w-0 flex-1">
                  <h3 className="text-sm font-semibold text-zinc-900 dark:text-zinc-100 truncate">
                    {menu.name}
                  </h3>
                  <div className="flex items-center gap-2 mt-1">
                    <span
                      className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${
                        menu.isActive
                          ? 'bg-[#EDFCF5] text-[#0E8965] dark:bg-[#0E8965]/20 dark:text-[#0E8965]'
                          : 'bg-zinc-100 text-zinc-500 dark:bg-zinc-700 dark:text-zinc-400'
                      }`}
                    >
                      {menu.isActive ? t('menus.active') : t('menus.inactive')}
                    </span>
                    <span className="text-xs text-zinc-500 dark:text-zinc-400">
                      {t('menus.itemCount', { count: menuItemCounts[menu.id] || 0 })}
                    </span>
                  </div>
                </div>

                {/* Kebab menu */}
                <div className="relative" ref={openKebabId === menu.id ? kebabRef : undefined}>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setOpenKebabId(openKebabId === menu.id ? null : menu.id);
                    }}
                    className="p-1 rounded-lg hover:bg-zinc-100 dark:hover:bg-zinc-700 text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-300"
                  >
                    <MoreVertical className="w-4 h-4" />
                  </button>

                  {openKebabId === menu.id && (
                    <div className="absolute right-0 top-full mt-1 bg-white dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-xl shadow-lg py-1 z-50 min-w-[160px]">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setOpenKebabId(null);
                          onEdit(menu);
                        }}
                        className="w-full flex items-center gap-2 px-3 py-2 text-sm text-zinc-700 dark:text-zinc-300 hover:bg-zinc-50 dark:hover:bg-zinc-700"
                      >
                        <Pencil className="w-4 h-4" />
                        {t('menus.editMenu')}
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setOpenKebabId(null);
                          onToggleActive(menu);
                        }}
                        className="w-full flex items-center gap-2 px-3 py-2 text-sm text-zinc-700 dark:text-zinc-300 hover:bg-zinc-50 dark:hover:bg-zinc-700"
                      >
                        {menu.isActive ? (
                          <ToggleLeft className="w-4 h-4" />
                        ) : (
                          <ToggleRight className="w-4 h-4" />
                        )}
                        {menu.isActive ? t('menus.inactive') : t('menus.active')}
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setOpenKebabId(null);
                          onDelete(menu);
                        }}
                        className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20"
                      >
                        <Trash2 className="w-4 h-4" />
                        {t('menus.deleteMenu')}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default MenuList;
